import React, { useState, useRef, useEffect } from 'react';
import { Box, Container, Typography, Chip, IconButton, useMediaQuery } from '@mui/material';
import { PlayArrow, OndemandVideo } from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';

/* ─────────────────────────────────────────────────────────
   VideoShowcase — Cinematic YouTube embed section
   Light mode: follows site design language — transparent bg,
   frosted glass, the dark video player provides its own contrast.
   Dark mode: immersive dark canvas with ambient glow.
   ───────────────────────────────────────────────────────── */

const YOUTUBE_ID = 'RG9MCJ_RZDo';

function VideoShowcase() {
  const { isLight } = useThemeMode();
  const isMobile = useMediaQuery('(max-width:900px)');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handlePlay = () => setIsPlaying(true);

  /* ── Design tokens ── */
  const accent = isLight ? '#6366f1' : '#10b981';
  const accentAlt = isLight ? '#818cf8' : '#38bdf8';

  return (
    <Box
      ref={sectionRef}
      component="section"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 6, md: 10 },
        /* Match other sections exactly: transparent in light, gradient in dark */
        background: isLight
          ? 'transparent'
          : 'linear-gradient(180deg, #020617 0%, #0a0f1f 15%, #0f172a 40%, #0f172a 60%, #0a0f1f 85%, #020617 100%)',
        backdropFilter: isLight ? 'blur(20px)' : undefined,
        WebkitBackdropFilter: isLight ? 'blur(20px)' : undefined,
      }}
    >
      {/* ── Ambient glow (dark mode only) ── */}
      {!isLight && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%',
            height: '60%',
            background:
              'radial-gradient(ellipse at center, rgba(16, 185, 129, 0.07) 0%, rgba(14, 165, 233, 0.03) 40%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
      )}

      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 2,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* ── Section header ── */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
          <Chip
            icon={<OndemandVideo sx={{ fontSize: '0.85rem' }} />}
            label="WATCH"
            size="small"
            sx={{
              mb: 2,
              px: 1.5,
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '1.5px',
              background: isLight
                ? 'rgba(255, 255, 255, 0.6)'
                : 'rgba(16, 185, 129, 0.1)',
              border: isLight
                ? '1px solid rgba(255, 255, 255, 0.8)'
                : '1px solid rgba(16, 185, 129, 0.25)',
              borderRadius: '50px',
              color: isLight ? '#1e293b' : '#6ee7b7',
              '& .MuiChip-icon': {
                color: isLight ? '#6366f1' : '#10b981',
              },
            }}
          />
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: '1.6rem', sm: '2rem', md: '2.6rem' },
              fontWeight: 800,
              lineHeight: 1.15,
              color: isLight ? '#0f172a' : '#ffffff',
              mb: 1.5,
              letterSpacing: '-0.02em',
            }}
          >
            See How{' '}
            <Box
              component="span"
              sx={{
                background: isLight
                  ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 40%, #818cf8 100%)'
                  : 'linear-gradient(135deg, #10b981 0%, #38bdf8 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              It Works
            </Box>
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '0.95rem', md: '1.05rem' },
              color: isLight ? 'rgba(15, 23, 42, 0.6)' : 'rgba(248, 250, 252, 0.6)',
              maxWidth: 540,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            From visual design to compiled deployment — the full Alphanetix story in under three minutes.
          </Typography>
        </Box>

        {/* ── Video container ── */}
        <Box
          sx={{
            position: 'relative',
            maxWidth: 960,
            mx: 'auto',
            /* Glassmorphic wrapper in light mode, cinematic in dark */
            borderRadius: { xs: '16px', md: '20px' },
            overflow: 'hidden',
            border: isLight
              ? '1px solid rgba(255, 255, 255, 0.7)'
              : '1px solid rgba(16, 185, 129, 0.2)',
            boxShadow: isLight
              ? '0 20px 60px rgba(99, 102, 241, 0.1), 0 8px 24px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255,255,255,0.5)'
              : '0 0 80px rgba(16, 185, 129, 0.08), 0 25px 60px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255,255,255,0.04)',
            aspectRatio: '16 / 9',
            background: isLight ? 'rgba(255,255,255,0.35)' : '#000',
            backdropFilter: isLight ? 'blur(16px)' : undefined,
            WebkitBackdropFilter: isLight ? 'blur(16px)' : undefined,
            /* Subtle padding creates a frosted glass "frame" in light mode */
            p: isLight ? { xs: '6px', md: '8px' } : 0,
          }}
        >
          {/* Inner video area — always dark, sits inside the glass frame */}
          <Box
            sx={{
              position: isLight ? 'relative' : 'absolute',
              inset: isLight ? undefined : 0,
              width: '100%',
              height: '100%',
              borderRadius: isLight ? { xs: '12px', md: '14px' } : 0,
              overflow: 'hidden',
              background: '#0a0a0a',
            }}
          >
            {!isPlaying ? (
              <Box
                onClick={handlePlay}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2,
                  '&:hover .play-btn': {
                    transform: 'translate(-50%, -50%) scale(1.1)',
                    boxShadow: isLight
                      ? '0 0 50px rgba(99, 102, 241, 0.5)'
                      : '0 0 60px rgba(16, 185, 129, 0.5)',
                  },
                  '&:hover .play-overlay': {
                    background: 'rgba(0, 0, 0, 0.3)',
                  },
                }}
              >
                {/* YouTube thumbnail */}
                <Box
                  component="img"
                  src={`https://img.youtube.com/vi/${YOUTUBE_ID}/maxresdefault.jpg`}
                  alt="Alphanetix platform introduction video"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  loading="lazy"
                />

                {/* Dark overlay */}
                <Box
                  className="play-overlay"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0, 0, 0, 0.4)',
                    transition: 'background 0.3s ease',
                  }}
                />

                {/* Pulse ring */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: 100, md: 130 },
                    height: { xs: 100, md: 130 },
                    borderRadius: '50%',
                    border: `2px solid ${isLight ? 'rgba(99, 102, 241, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`,
                    animation: 'videoPulse 2.5s ease-in-out infinite',
                    '@keyframes videoPulse': {
                      '0%, 100%': { transform: 'translate(-50%, -50%) scale(1)', opacity: 0.6 },
                      '50%': { transform: 'translate(-50%, -50%) scale(1.2)', opacity: 0 },
                    },
                  }}
                />

                {/* Play button */}
                <IconButton
                  className="play-btn"
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: 68, md: 84 },
                    height: { xs: 68, md: 84 },
                    background: isLight
                      ? 'rgba(99, 102, 241, 0.9)'
                      : 'rgba(16, 185, 129, 0.9)',
                    backdropFilter: 'blur(12px)',
                    border: '2px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: isLight
                      ? '0 0 40px rgba(99, 102, 241, 0.4), 0 8px 30px rgba(0, 0, 0, 0.2)'
                      : '0 0 40px rgba(16, 185, 129, 0.4), 0 8px 30px rgba(0, 0, 0, 0.3)',
                    transition: 'all 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    '&:hover': {
                      background: isLight
                        ? 'rgba(79, 70, 229, 1)'
                        : 'rgba(16, 185, 129, 1)',
                    },
                  }}
                >
                  <PlayArrow sx={{ fontSize: { xs: 34, md: 42 }, color: '#fff', ml: '3px' }} />
                </IconButton>
              </Box>
            ) : (
              <Box
                component="iframe"
                src={`https://www.youtube-nocookie.com/embed/${YOUTUBE_ID}?autoplay=1&rel=0&modestbranding=1&color=white`}
                title="Alphanetix — Platform Introduction"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
              />
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default VideoShowcase;
